import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import { allMedia } from "@/utils/media";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const TYPE_TABS = ["全部", "电影", "电视剧"] as const;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [typeTab, setTypeTab] = useState<(typeof TYPE_TABS)[number]>("全部");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useSeo({
    title: `搜索“${keyword}” - 飘花电影网`,
    description: `飘花电影网搜索结果页，展示与“${keyword}”相关的影视内容，支持按电影、电视剧类别筛选。`,
    keywords: `${keyword},搜索,影视资源,飘花电影网`,
  });

  const mediaResult = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return allMedia.filter((item) => {
      const haystack = `${item.title} ${item.genres.join(" ")} ${item.region} ${item.cast.join(" ")}`.toLowerCase();
      const matched = haystack.includes(kw);
      if (!matched) return false;
      if (typeTab === "电影") return item.type === "movie";
      if (typeTab === "电视剧") return item.type === "tv";
      return true;
    });
  }, [keyword, typeTab]);

  const newsResult = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return [];
    return newsList.filter((item) => item.title.includes(kw) || item.summary.includes(kw));
  }, [keyword]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const total = mediaResult.length + newsResult.length;

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: "搜索" }]} />

        <div className="mt-4 rounded-xl border border-background-200 bg-background-50 p-4 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">搜索影视内容</h1>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                placeholder="输入影片名称、类型或地区"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              搜索
            </button>
          </form>

          {keyword && (
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
              <p className="text-sm text-foreground-500">
                关键词：<span className="font-semibold text-primary-700">“{keyword}”</span>
              </p>
              <span className="text-sm text-foreground-400">共找到 {total} 条相关结果</span>
            </div>
          )}
        </div>

        {keyword && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setTypeTab(tab)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  typeTab === tab
                    ? "bg-primary-600 font-medium text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        {!keyword ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-3xl" />
            </span>
            <p className="mt-4 font-medium text-foreground-700">请输入关键词开始搜索</p>
            <p className="mt-1 text-sm text-foreground-400">可搜索影片名称、类型或地区，如“科幻”“大陆”</p>
          </div>
        ) : total === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-unhappy-line text-3xl" />
            </span>
            <p className="mt-4 font-medium text-foreground-700">没有找到与“{keyword}”相关的内容</p>
            <p className="mt-1 text-sm text-foreground-400">试试更换关键词，或浏览以下推荐</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link to="/movie" className="whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-700">
                浏览电影
              </Link>
              <Link to="/tv" className="whitespace-nowrap rounded-md bg-secondary-600 px-4 py-2 text-sm font-medium text-background-50 hover:bg-secondary-700">
                浏览电视剧
              </Link>
              <Link to="/news" className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:border-primary-400">
                查看资讯
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {mediaResult.length > 0 && (
              <section>
                <h2 className="mb-4 flex items-center gap-2 border-b-2 border-background-200 pb-2.5 font-heading text-lg font-bold text-foreground-900">
                  <span className="h-5 w-1 rounded-full bg-primary-600" />
                  影视结果（{mediaResult.length}）
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                  {mediaResult.map((item) => (
                    <MediaCard key={item.id} media={item} />
                  ))}
                </div>
              </section>
            )}

            {newsResult.length > 0 && (
              <section>
                <h2 className="mb-4 flex items-center gap-2 border-b-2 border-background-200 pb-2.5 font-heading text-lg font-bold text-foreground-900">
                  <span className="h-5 w-1 rounded-full bg-accent-500" />
                  资讯结果（{newsResult.length}）
                </h2>
                <div className="space-y-3">
                  {newsResult.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 px-4 py-3 transition-all hover:border-primary-300"
                    >
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </h3>
                        <p className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                          <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-700">{item.category}</span>
                          <span>{item.date}</span>
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-foreground-300" />
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {mediaResult.length === 0 && newsResult.length === 0 && (
              <p className="text-center text-sm text-foreground-400">暂无结果</p>
            )}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}